<script lang="ts">
  import { onMount } from 'svelte';
  import {
    getAccount,
    totpSetup,
    totpConfirm,
    totpDisable,
    changePassword,
    logoutAll,
    listIdentities,
    unlinkGoogle,
    GOOGLE_LINK_PATH,
    type TotpSetup,
    type OAuthIdentity,
  } from '../account';
  import { session } from '../session.svelte';
  import { ApiError } from '../api';

  let mfaEnabled = $state<boolean | null>(null);
  let loadFailed = $state(false);
  let err = $state('');

  // linked sign-in methods
  let identities = $state<OAuthIdentity[] | null>(null);
  const google = $derived(identities?.find((i) => i.provider === 'google') ?? null);
  let unlinking = $state(false);

  async function refreshIdentities() {
    try {
      identities = (await listIdentities()).identities;
    } catch {
      identities = [];
    }
  }

  async function disconnectGoogle() {
    if (unlinking) return;
    unlinking = true;
    err = '';
    try {
      await unlinkGoogle();
      await refreshIdentities();
    } catch (e) {
      err = e instanceof ApiError ? e.message : 'Could not disconnect Google.';
    } finally {
      unlinking = false;
    }
  }

  // enrolment flow
  let setup = $state<TotpSetup | null>(null);
  let code = $state('');
  let busy = $state(false);

  // disable flow
  let disabling = $state(false);
  let password = $state('');

  // change-password flow
  let pwOpen = $state(false);
  let curPw = $state('');
  let newPw = $state('');
  let pwCode = $state('');
  let pwDone = $state(false);

  onMount(async () => {
    try {
      mfaEnabled = (await getAccount()).mfa_enabled;
    } catch {
      loadFailed = true;
    }
    await refreshIdentities();

    // Returning from the Google link flow: ?oauth=linked | already_linked
    const p = new URLSearchParams(location.search);
    const o = p.get('oauth');
    if (o === 'already_linked') err = 'That Google account is linked to a different Kip account.';
    if (o) {
      p.delete('oauth');
      history.replaceState({}, '', location.pathname + (p.toString() ? `?${p}` : ''));
    }
  });

  async function startSetup() {
    err = '';
    busy = true;
    try {
      setup = await totpSetup();
    } catch (e) {
      err = e instanceof ApiError ? e.message : 'Could not start setup.';
    } finally {
      busy = false;
    }
  }

  async function confirm() {
    if (code.trim().length < 6 || busy) return;
    busy = true;
    err = '';
    try {
      await totpConfirm(code.trim());
      mfaEnabled = true;
      setup = null;
      code = '';
    } catch (e) {
      err = e instanceof ApiError ? e.message : 'That code did not work.';
      code = '';
    } finally {
      busy = false;
    }
  }

  async function disable() {
    if (!password || busy) return;
    busy = true;
    err = '';
    try {
      await totpDisable(password);
      mfaEnabled = false;
      disabling = false;
      password = '';
    } catch (e) {
      err = e instanceof ApiError ? e.message : 'Password is incorrect.';
    } finally {
      busy = false;
    }
  }

  async function signOutEverywhere() {
    await logoutAll().catch(() => {});
    await session.logout();
  }

  async function submitPassword() {
    if (busy || !curPw || newPw.length < 10) return;
    if (mfaEnabled && pwCode.trim().length < 6) return;
    busy = true;
    err = '';
    try {
      await changePassword(curPw, newPw, mfaEnabled ? pwCode.trim() : undefined);
      curPw = newPw = pwCode = '';
      pwOpen = false;
      pwDone = true;
      setTimeout(() => (pwDone = false), 3000);
    } catch (e) {
      err = e instanceof ApiError ? e.message : 'Could not change the password.';
    } finally {
      busy = false;
    }
  }
</script>

<div class="card">
  <p class="eyebrow">Security</p>

  {#if loadFailed}
    <p class="muted">Couldn't load your security settings.</p>
  {:else if mfaEnabled === null}
    <p class="muted">Loading…</p>
  {:else if setup}
    <p class="muted">Scan with an authenticator app, then enter the 6-digit code.</p>
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    <div class="qr">{@html setup.qr_svg}</div>
    <p class="secret">{setup.secret}</p>
    <div class="row">
      <input
        bind:value={code}
        inputmode="numeric"
        maxlength="6"
        placeholder="000000"
        class="totp"
      />
      <button class="cta" onclick={confirm} disabled={busy || code.trim().length < 6}
        >Confirm</button
      >
    </div>
    <button class="ghost" onclick={() => (setup = null)}>Cancel</button>
  {:else if mfaEnabled}
    <p class="value">Two-factor is on.</p>
    {#if disabling}
      <div class="row">
        <input bind:value={password} type="password" placeholder="Your password" />
        <button class="cta danger" onclick={disable} disabled={busy || !password}>Turn off</button>
      </div>
      <button class="ghost" onclick={() => (disabling = false)}>Cancel</button>
    {:else}
      <button class="ghost" onclick={() => (disabling = true)}>Turn off two-factor</button>
    {/if}
  {:else}
    <p class="muted">Add a second factor with an authenticator app.</p>
    <button class="cta" onclick={startSetup} disabled={busy}>Set up two-factor</button>
  {/if}

  {#if !setup && mfaEnabled !== null && !loadFailed}
    <hr />
    {#if pwDone}
      <p class="value">Password changed. Other devices were signed out.</p>
    {:else if pwOpen}
      <form
        onsubmit={(e) => {
          e.preventDefault();
          void submitPassword();
        }}
      >
        <input
          bind:value={curPw}
          type="password"
          autocomplete="current-password"
          placeholder="Current password"
        />
        <input
          bind:value={newPw}
          type="password"
          autocomplete="new-password"
          placeholder="New password (10+ characters)"
        />
        {#if mfaEnabled}
          <input
            bind:value={pwCode}
            inputmode="numeric"
            maxlength="6"
            placeholder="Authenticator code"
            class="totp"
          />
        {/if}
        <div class="row">
          <button
            class="cta"
            type="submit"
            disabled={busy ||
              !curPw ||
              newPw.length < 10 ||
              (mfaEnabled && pwCode.trim().length < 6)}
          >
            {busy ? 'Changing…' : 'Change password'}
          </button>
          <button class="ghost" type="button" onclick={() => (pwOpen = false)}>Cancel</button>
        </div>
      </form>
    {:else}
      <button class="ghost" onclick={() => (pwOpen = true)}>Change password</button>
    {/if}
  {/if}

  {#if identities !== null && session.state.oauth?.google}
    <hr />
    <p class="value">Sign in with Google</p>
    {#if google}
      <p class="muted">Connected as {google.email}.</p>
      <button class="ghost" onclick={disconnectGoogle} disabled={unlinking}>
        {unlinking ? 'Disconnecting…' : 'Disconnect'}
      </button>
    {:else}
      <p class="muted">Use your Google account to sign in.</p>
      <a class="ghost linkbtn" href={GOOGLE_LINK_PATH}>Connect Google</a>
    {/if}
  {/if}

  <hr />
  <button class="ghost" onclick={signOutEverywhere}>Sign out on all devices</button>

  {#if err}<p class="err">{err}</p>{/if}
</div>

<style>
  .card {
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 16px;
    margin-bottom: 14px;
    background: var(--cream-2);
    box-shadow: var(--shadow-soft);
  }
  .eyebrow {
    margin-bottom: 6px;
  }
  .value {
    font-size: 15px;
  }
  .muted {
    color: var(--ink-soft);
    font-size: 14px;
    margin: 4px 0;
  }
  .qr {
    width: 160px;
    margin: 10px 0;
  }
  .qr :global(svg) {
    width: 100%;
    height: auto;
    display: block;
  }
  .secret {
    font-family: var(--mono);
    font-size: 12px;
    letter-spacing: 0.1em;
    color: var(--ink-soft);
    word-break: break-all;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 10px 0;
  }
  .row {
    display: flex;
    gap: 8px;
    margin: 10px 0;
  }
  form .row {
    margin: 2px 0 0;
  }
  .row input,
  form > input {
    flex: 1;
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 9px 12px;
    background: var(--cream-2);
    font-size: 16px;
    min-width: 0;
  }
  .totp {
    font-family: var(--mono);
    letter-spacing: 0.3em;
    text-align: center;
  }
  .cta,
  .ghost {
    margin-top: 8px;
    border-radius: 10px;
    padding: 9px 14px;
    font-family: var(--mono);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.03em;
    white-space: nowrap;
  }
  .cta {
    background: var(--rooster);
    color: var(--cream);
    margin-top: 0;
  }
  .cta.danger {
    background: var(--rooster-deep);
  }
  .cta:disabled {
    opacity: 0.4;
  }
  .ghost {
    border: 1px solid var(--line);
    color: var(--ink-soft);
  }
  a.linkbtn {
    display: inline-block;
    text-decoration: none;
  }
  hr {
    border: none;
    border-top: 1px solid var(--line);
    margin: 16px 0 4px;
  }
  .err {
    color: var(--rooster-deep);
    font-size: 13px;
    margin-top: 10px;
  }
</style>
